/**
 * ESCHER FALLS — 水の充填アニメーション（MODULE-CONTRACTS §8）。
 *
 * 時間の権威は `FlowPlan` であり、ここでは物理シミュレーションを一切行わない。
 * `update(dt)` は内部時刻を進め、各 `FlowStep` の充填率を `WaterMesh.setFill()` に流し込むだけ。
 * パーティクル（落水のしぶき／行き止まりのこぼれ）だけは見た目のための簡易積分を持つが、
 * これは接続判定にも状態遷移にも影響しない純粋な演出である。
 */

import * as THREE from 'three';
import type { FlowPlan, FlowStep, SpillEvent } from '../core/types';
import { SPILL_DURATION_MS } from '../core/types';
import type { WaterMesh } from './meshes';
import { getPortAnchors, getWaterMeshes, getWheelMeshes } from './meshes';
import { PALETTE } from './materials';

// =============================================================================
// チューニング定数（すべて見た目のみに影響する）
// =============================================================================

/** パーティクルの同時生存上限。落水1本あたり 30 個前後で釣り合うよう選んである。 */
const PARTICLE_CAPACITY = 160;
/** 点のサイズ（**ピクセル**）。正射影では sizeAttenuation を切らないと奥行きで大きさが暴れる。 */
const PARTICLE_SIZE_PX = 5;
/** パーティクルにかける重力（ワールド単位 / 秒²）。実測値ではなく見た目重視。 */
const PARTICLE_GRAVITY = -14;

/** 落水セグメントが充填中に出すしぶきの発生レート（個/秒）。 */
const FALL_EMIT_PER_SEC = 45;
/** 落水しぶきの寿命 (ms)。レートと掛けて同時生存 ≈ 31 個。 */
const FALL_PARTICLE_LIFE_MS = 700;
/** 落水が下端に届いた瞬間に散る飛沫の個数。 */
const SPLASH_BURST = 10;
/** 飛沫の寿命 (ms)。 */
const SPLASH_LIFE_MS = 450;

/** 行き止まりのこぼれの発生レート（個/秒）。 */
const SPILL_EMIT_PER_SEC = 32;
/**
 * こぼれの発生を打ち切る残り時間 (ms)。
 * 各粒子の寿命を「こぼれ窓の残り時間」にしているので、これ未満では粒子が一瞬で消えて汚い。
 */
const SPILL_EMIT_CUTOFF_MS = 200;

/** 水車の角速度 (rad/秒)。 */
const WHEEL_SPEED = 3.2;

// =============================================================================
// 公開インターフェース（MODULE-CONTRACTS §8）
// =============================================================================

export interface WaterProgress {
  /** 放水開始からの経過時間 (ms)。 */
  readonly time: number;
  /** `plan.durationMs` に達したか。 */
  readonly finished: boolean;
  /** これまでにゴール到達時刻を越えたか。 */
  readonly goalReached: boolean;
  /** この update 呼び出しで初めてゴールに到達したか。 */
  readonly justReachedGoal: boolean;
}

export interface WaterAnimator {
  readonly isPlaying: boolean;
  /** 再生を開始する。内部時刻を 0 に戻し、全 WaterMesh の fill を 0 にする。 */
  play(plan: FlowPlan): void;
  /** dt (ms) 進める。plan 未設定なら time=0, finished=true 相当を返す。 */
  update(dt: number): WaterProgress;
  /** 全 WaterMesh を空にし、パーティクルを消し、plan を捨てる。 */
  reset(): void;
  /** クリア演出（水をアクセント色に）を有効化する。 */
  setClear(on: boolean): void;
  dispose(): void;
}

// =============================================================================
// 内部ユーティリティ
// =============================================================================

const IDLE_PROGRESS: WaterProgress = {
  time: 0,
  finished: true,
  goalReached: false,
  justReachedGoal: false,
};

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** `"p3:top"` → `"p3"`、`"p3:seg"` → `"p3"`。ピースIDに `:` が含まれても壊れないよう後ろから切る。 */
function ownerId(id: string): string {
  const i = id.lastIndexOf(':');
  return i < 0 ? id : id.slice(0, i);
}

/** `"p3:top"` → `"top"`。 */
function memberName(id: string): string {
  const i = id.lastIndexOf(':');
  return i < 0 ? id : id.slice(i + 1);
}

/**
 * 決定的な擬似乱数（mulberry32）。
 * `Math.random()` を使うと同じ盤面のリプレイで絵が変わり、目視デバッグが困難になる。
 */
function makeRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * 水車の回転軸（ワールド空間）。
 * MODULE-CONTRACTS §7.2「軸は `def.dir` に直交する水平軸」に従う。
 * 通水方向が水平なら軸はそれに直交するもう一方の水平軸、
 * 垂直（既定の `'+y'` 水車）なら水平軸は一意に決まらないので **+X** を採る。
 */
function spinAxisFor(flow: THREE.Vector3): THREE.Vector3 {
  const ax = Math.abs(flow.x);
  const az = Math.abs(flow.z);
  const ay = Math.abs(flow.y);
  if (ax >= ay && ax >= az) return new THREE.Vector3(0, 0, 1);
  if (az >= ay && az >= ax) return new THREE.Vector3(1, 0, 0);
  return new THREE.Vector3(1, 0, 0);
}

// =============================================================================
// パーティクル（THREE.Points 1枚だけ。使用中の粒子を配列先頭に詰めて drawRange で描く）
// =============================================================================

class ParticleField {
  readonly points: THREE.Points;

  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.PointsMaterial;
  private readonly posAttr: THREE.BufferAttribute;
  private readonly pos: Float32Array;
  private readonly vel: Float32Array;
  private readonly life: Float32Array;
  private readonly capacity: number;
  private count = 0;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.pos = new Float32Array(capacity * 3);
    this.vel = new Float32Array(capacity * 3);
    this.life = new Float32Array(capacity);

    this.posAttr = new THREE.BufferAttribute(this.pos, 3);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', this.posAttr);
    this.geometry.setDrawRange(0, 0);

    this.material = new THREE.PointsMaterial({
      color: PALETTE.water,
      size: PARTICLE_SIZE_PX,
      sizeAttenuation: false,
      depthWrite: false,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.name = 'water-particles';
    // drawRange で描画数を変えるため境界球が当てにならない。カリングを切る。
    this.points.frustumCulled = false;
    this.points.renderOrder = 2;
  }

  spawn(
    x: number, y: number, z: number,
    vx: number, vy: number, vz: number,
    lifeMs: number,
  ): void {
    if (this.count >= this.capacity || lifeMs <= 0) return;
    const i = this.count++;
    const j = i * 3;
    this.pos[j] = x;
    this.pos[j + 1] = y;
    this.pos[j + 2] = z;
    this.vel[j] = vx;
    this.vel[j + 1] = vy;
    this.vel[j + 2] = vz;
    this.life[i] = lifeMs;
  }

  update(dtMs: number): void {
    const dt = dtMs / 1000;
    let i = 0;
    while (i < this.count) {
      const rest = this.life[i] - dtMs;
      if (rest <= 0) {
        this.swapRemove(i);
        continue; // 詰めた粒子を同じ添字で処理し直す
      }
      this.life[i] = rest;
      const j = i * 3;
      this.vel[j + 1] += PARTICLE_GRAVITY * dt;
      this.pos[j] += this.vel[j] * dt;
      this.pos[j + 1] += this.vel[j + 1] * dt;
      this.pos[j + 2] += this.vel[j + 2] * dt;
      i++;
    }
    this.geometry.setDrawRange(0, this.count);
    this.posAttr.needsUpdate = true;
  }

  setAccent(on: boolean): void {
    this.material.color.setHex(on ? PALETTE.accent : PALETTE.water);
  }

  clear(): void {
    this.count = 0;
    this.geometry.setDrawRange(0, 0);
    this.posAttr.needsUpdate = true;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }

  /** 末尾の生存粒子を i に詰めて 1 つ縮める。 */
  private swapRemove(i: number): void {
    const last = this.count - 1;
    if (i !== last) {
      const a = i * 3;
      const b = last * 3;
      this.pos[a] = this.pos[b];
      this.pos[a + 1] = this.pos[b + 1];
      this.pos[a + 2] = this.pos[b + 2];
      this.vel[a] = this.vel[b];
      this.vel[a + 1] = this.vel[b + 1];
      this.vel[a + 2] = this.vel[b + 2];
      this.life[i] = this.life[last];
    }
    this.count = last;
  }
}

// =============================================================================
// 再生時に一度だけ組む実行時データ
// =============================================================================

interface StepRuntime {
  readonly step: FlowStep;
  readonly mesh: WaterMesh | null;
  /** 水が入ってくる側のポート位置（複製済み）。 */
  readonly from: THREE.Vector3 | null;
  /** 水が出ていく側のポート位置（複製済み）。 */
  readonly to: THREE.Vector3 | null;
  /** `fall` ピースのセグメントか（ポート名が top/bottom なのは fall だけ）。 */
  readonly isFall: boolean;
  readonly wheel: THREE.Object3D | null;
  readonly wheelAxis: THREE.Vector3;
  /** 前フレームの充填率。立ち上がり検出に使う。 */
  fill: number;
  /** 発生数の端数キャリー。 */
  emitAcc: number;
}

interface SpillRuntime {
  readonly event: SpillEvent;
  readonly at: THREE.Vector3 | null;
  emitAcc: number;
}

// =============================================================================
// 本体
// =============================================================================

export function createWaterAnimator(scene: THREE.Scene): WaterAnimator {
  const field = new ParticleField(PARTICLE_CAPACITY);
  scene.add(field.points);

  const rng = makeRng(0x9e3779b9);
  const tmp = new THREE.Vector3();

  let plan: FlowPlan | null = null;
  let time = 0;
  let goalReached = false;
  let clearOn = false;
  let steps: StepRuntime[] = [];
  let spills: SpillRuntime[] = [];

  /** 水車の Object3D を引く。契約のレジストリが本筋、名前 `'wheel'` 探索は保険。 */
  function findWheel(pieceId: string): THREE.Object3D | null {
    const registered = getWheelMeshes().get(pieceId);
    if (registered) return registered;
    const piece = scene.getObjectByName(pieceId);
    return piece?.getObjectByName('wheel') ?? null;
  }

  function buildStepRuntime(step: FlowStep): StepRuntime {
    const anchors = getPortAnchors();
    const from = anchors.get(step.fromPort);
    const to = anchors.get(step.toPort);
    const name = memberName(step.fromPort);
    const pieceId = ownerId(step.segmentId);
    const wheel = findWheel(pieceId);

    const fromV = from ? from.clone() : null;
    const toV = to ? to.clone() : null;
    const axis = fromV && toV
      ? spinAxisFor(tmp.copy(toV).sub(fromV))
      : new THREE.Vector3(1, 0, 0);

    return {
      step,
      mesh: getWaterMeshes().get(step.segmentId) ?? null,
      from: fromV,
      to: toV,
      isFall: name === 'top' || name === 'bottom',
      wheel,
      wheelAxis: axis,
      fill: 0,
      emitAcc: 0,
    };
  }

  /** 落水の水面（充填の先端）からしぶきを落とす。 */
  function emitFall(r: StepRuntime, dt: number): void {
    if (!r.from || !r.to) return;
    r.emitAcc += FALL_EMIT_PER_SEC * (dt / 1000);
    while (r.emitAcc >= 1) {
      r.emitAcc -= 1;
      tmp.copy(r.from).lerp(r.to, r.fill);
      field.spawn(
        tmp.x + (rng() - 0.5) * 0.22,
        tmp.y + (rng() - 0.5) * 0.12,
        tmp.z + (rng() - 0.5) * 0.22,
        (rng() - 0.5) * 0.5,
        -1.2 - rng() * 1.6,
        (rng() - 0.5) * 0.5,
        FALL_PARTICLE_LIFE_MS,
      );
    }
  }

  /** 落水が下端に届いた瞬間の飛沫。 */
  function emitSplash(at: THREE.Vector3): void {
    for (let i = 0; i < SPLASH_BURST; i++) {
      const a = rng() * Math.PI * 2;
      const s = 0.6 + rng() * 0.8;
      field.spawn(
        at.x, at.y, at.z,
        Math.cos(a) * s,
        0.7 + rng() * 1.1,
        Math.sin(a) * s,
        SPLASH_LIFE_MS,
      );
    }
  }

  /** 行き止まりのポートから真下へこぼす。 */
  function emitSpill(s: SpillRuntime, dt: number, remaining: number): void {
    if (!s.at) return;
    s.emitAcc += SPILL_EMIT_PER_SEC * (dt / 1000);
    while (s.emitAcc >= 1) {
      s.emitAcc -= 1;
      field.spawn(
        s.at.x + (rng() - 0.5) * 0.18,
        s.at.y,
        s.at.z + (rng() - 0.5) * 0.18,
        (rng() - 0.5) * 0.35,
        -0.4 - rng() * 0.6,
        (rng() - 0.5) * 0.35,
        remaining,
      );
    }
  }

  function play(next: FlowPlan): void {
    // 直前の再生が残していた水とクリア演出を消してから組み直す。
    if (clearOn) setClear(false);
    for (const wm of getWaterMeshes().values()) {
      wm.setReversed(false);
      wm.setFill(0);
    }
    field.clear();

    plan = next;
    time = 0;
    goalReached = false;
    steps = next.steps.map(buildStepRuntime);
    spills = next.spills.map((event) => ({
      event,
      at: getPortAnchors().get(event.portId)?.clone() ?? null,
      emitAcc: 0,
    }));
  }

  function update(dt: number): WaterProgress {
    if (plan === null) return IDLE_PROGRESS;

    const step = dt > 0 ? dt : 0;
    time += step;

    for (const r of steps) {
      const d = r.step.duration;
      const fill = d > 0
        ? clamp01((time - r.step.startTime) / d)
        : (time >= r.step.startTime ? 1 : 0);

      if (r.mesh) {
        r.mesh.setReversed(r.step.reversed);
        r.mesh.setFill(fill);
      }

      const prev = r.fill;
      r.fill = fill;

      if (r.isFall) {
        if (fill > 0 && fill < 1) emitFall(r, step);
        if (prev < 1 && fill >= 1 && r.to) emitSplash(r.to);
      }

      // 水車は「そのセグメントに水がある間」だけ回る（充填中・満水とも）。
      if (r.wheel && fill > 0) {
        const sign = r.step.reversed ? -1 : 1;
        r.wheel.rotateOnWorldAxis(r.wheelAxis, WHEEL_SPEED * (step / 1000) * sign);
      }
    }

    for (const s of spills) {
      const end = s.event.time + SPILL_DURATION_MS;
      if (time < s.event.time || time >= end) continue;
      const remaining = end - time;
      if (remaining <= SPILL_EMIT_CUTOFF_MS) continue;
      emitSpill(s, step, remaining);
    }

    field.update(step);

    const hit = plan.reachesGoal && plan.goalTime !== null && time >= plan.goalTime;
    const justReachedGoal = hit && !goalReached;
    goalReached = goalReached || hit;

    return {
      time,
      finished: time >= plan.durationMs,
      goalReached,
      justReachedGoal,
    };
  }

  function reset(): void {
    plan = null;
    steps = [];
    spills = [];
    time = 0;
    goalReached = false;
    clearOn = false;
    field.clear();
    field.setAccent(false);
    for (const wm of getWaterMeshes().values()) {
      wm.setReversed(false);
      wm.setFill(0);
      wm.setAccent(false);
    }
  }

  function setClear(on: boolean): void {
    clearOn = on;
    field.setAccent(on);
    for (const wm of getWaterMeshes().values()) wm.setAccent(on);
  }

  function dispose(): void {
    field.clear();
    scene.remove(field.points);
    field.dispose();
    plan = null;
    steps = [];
    spills = [];
  }

  return {
    get isPlaying(): boolean {
      return plan !== null && time < plan.durationMs;
    },
    play,
    update,
    reset,
    setClear,
    dispose,
  };
}
